import React from 'react';
import { Platform, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTerms } from '@/hooks/use-terms';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';
import { usePlanLimitStore } from '@/store/planLimitStore';
import type {
  PlanLimitExceededPayload,
  PlanSeatsExceededPayload,
} from '@/utils/parse-plan-limit-error';

const plansRouter = { push: (path: string) => router.push(path as never) };

/**
 * Aviso de upgrade quando o plano trava cliente novo ou profissional novo.
 * Montado uma vez no layout raiz; qualquer tela abre com `showPlanLimit(err)`.
 */
export function PlanLimitModal() {
  const payload = usePlanLimitStore((s) => s.payload);
  const close = usePlanLimitStore((s) => s.close);

  if (!payload) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="w-[90%] max-w-md">
        {payload.code === 'PLAN_SEATS_EXCEEDED' ? (
          <SeatsBody payload={payload} close={close} />
        ) : (
          <ClientsBody payload={payload} close={close} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Header({ title }: { title: string }) {
  return (
    <>
      <View className="mb-4 h-14 w-14 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
        <Ionicons name="people-outline" size={28} className="text-primary dark:text-white" />
      </View>
      <DialogHeader className="items-center">
        <DialogTitle className="text-center text-xl text-foreground">{title}</DialogTitle>
      </DialogHeader>
    </>
  );
}

function ClientsBody({ payload, close }: { payload: PlanLimitExceededPayload; close: () => void }) {
  const handleVerPlanos = () => {
    close();
    void navigateToSubscriptionPlans(plansRouter);
  };

  return (
    <View className="items-center pt-2">
      <Header title="Seu plano está no limite" />
      <View className="mt-3 gap-2 px-1">
        <ThemedText className="text-muted-foreground text-center">
          Você já atendeu {payload.used} clientes este mês no plano {payload.planName}, que cobre
          até {payload.limit}.
        </ThemedText>
        <ThemedText className="text-muted-foreground text-center text-sm">
          Quem já é seu cliente continua marcando normalmente. Para receber clientes novos, faça
          upgrade do plano.
        </ThemedText>
      </View>
      <DialogFooter className="mt-6 w-full flex-row gap-3">
        <Button title="Entendi" variant="outline" className="flex-1" onPress={close} />
        <Button title="Ver planos" className="flex-1" onPress={handleVerPlanos} />
      </DialogFooter>
    </View>
  );
}

/** Faltou assento: leva à calculadora já com o número de profissionais pedido. */
function SeatsBody({ payload, close }: { payload: PlanSeatsExceededPayload; close: () => void }) {
  const terms = useTerms();

  const handleMontarPlano = () => {
    close();
    // Android contrata pela web (sem os parâmetros); web e iPhone ficam no app.
    if (Platform.OS === 'android') {
      void navigateToSubscriptionPlans(plansRouter);
      return;
    }
    router.push({
      pathname: '/subscription/plans',
      params: { professionals: String(payload.requested) },
    } as never);
  };

  return (
    <View className="items-center pt-2">
      <Header
        title={`Falta espaço para mais ${terms.agree('um', 'professional')} ${terms.professional}`}
      />
      <View className="mt-3 gap-2 px-1">
        <ThemedText className="text-muted-foreground text-center">
          {payload.limit
            ? `O plano da equipe cobre ${payload.limit} ${terms.professionalPlural}. Para ter ${payload.requested}, ajuste o plano.`
            : `Para ter ${payload.requested} ${terms.professionalPlural} atendendo, monte o plano da equipe.`}
        </ThemedText>
      </View>
      <DialogFooter className="mt-6 w-full flex-row gap-3">
        <Button title="Agora não" variant="outline" className="flex-1" onPress={close} />
        <Button title="Montar plano" className="flex-1" onPress={handleMontarPlano} />
      </DialogFooter>
    </View>
  );
}
